/**
 * TpinDashPage - S-DASH, the per-TPIN dashboard (UX-RW-03 v1.1 S-DASH; RW-01
 * S1, S2, S5, S6; ADR-005 v1.6 sections 3.2 / 3.3).
 *
 * Landing for a selected TPIN is ALWAYS this overview (D-13/R-2): a single
 * branch is offered as a link, never as a redirect. Combined stats come from
 * `/summary` (reachable branches only, coverage-qualified); the FULL branch
 * list comes from `/branches`, paged by cursor. The period lives in the URL;
 * a period change is a new scope and reloads both surfaces from page 1. The
 * only re-read is the Refresh button (PD-33): it re-queries this scope only.
 *
 * An invalid custom range is rejected inline BEFORE any query (FR-5): the
 * store is not opened and no request is made.
 */

import { useEffect } from 'react'
import { AppHeader } from '@components/AppHeader'
import { BranchList } from '@components/BranchList'
import { Breadcrumbs } from '@components/Breadcrumbs'
import { ContextHero } from '@components/ContextHero'
import { CoverageLine, DistributionBar } from '@components/CoverageDistribution'
import { KpiTrio } from '@components/KpiTrio'
import { Link } from '@components/Link'
import { ErrorState, LoadingState, RefreshingFrame } from '@components/RealStates'
import { routePeriodUsable, useRoute } from '@components/route'
import { branchesFor, freshnessOf, isRefreshing, scopeText, singleBranchOf, summaryFor, totalOf } from '@domain/tpinDash'
import { branchPath } from '@domain/tpinPaths'
import { rememberScrollOnLeave, restoreScroll, scrollKey } from '@lib/scrollMemory'
import { useTpinOverviewStore } from '@stores/tpinOverviewStore'

export function TpinDashPage({ tin }: { readonly tin: string }) {
  const route = useRoute()
  const period = route.period
  const verdict = routePeriodUsable(period)
  // An unusable range is the empty string: no real date is empty, so one test says it all.
  // `to` follows `from` when unusable, so no second literal can drift from the first.
  const from = verdict.usable ? verdict.resolved.from : ''
  const to = verdict.usable ? verdict.resolved.to : from

  const summary = useTpinOverviewStore((s) => summaryFor(s, tin, from, to))
  const branches = useTpinOverviewStore((s) => branchesFor(s, tin, from, to))
  const open = useTpinOverviewStore((s) => s.open)
  const cancel = useTpinOverviewStore((s) => s.cancel)
  const refresh = useTpinOverviewStore((s) => s.refresh)
  const retrySummary = useTpinOverviewStore((s) => s.retrySummary)
  const retryBranches = useTpinOverviewStore((s) => s.retryBranches)
  const loadMore = useTpinOverviewStore((s) => s.loadMore)

  useEffect(() => {
    if (from === '') return
    open(tin, { from, to })
    return cancel
  }, [tin, from, to, open, cancel])

  // One-Window Drilldown: leaving for a branch remembers the list position, and
  // coming back to the same scope once its branches are on screen puts it back.
  const key = scrollKey({ tin, from, to })
  const branchesReady = branches.kind === 'ready'
  useEffect(() => {
    if (branchesReady) restoreScroll(key)
  }, [key, branchesReady])
  useEffect(() => () => rememberScrollOnLeave(key, window.location.pathname, window.scrollY), [key])

  const total = totalOf(summary)
  const shortcut = singleBranchOf(summary, branches)
  const refreshing = isRefreshing(summary, branches)

  return (
    <div className="page page-dash" data-testid="dash-page">
      <AppHeader period={period} />
      <main className="page-main dash-main">
        <Breadcrumbs crumbs={[{ label: 'TPIN directory', to: '/' }, { label: `TPIN ${tin}` }]} period={period} />
        <ContextHero
          tin={tin}
          branchesTotal={total}
          period={period}
          freshness={freshnessOf(summary)}
          refreshing={refreshing}
          onRefresh={refresh}
        />

        {!verdict.usable ? (
          <ErrorState message={verdict.reason} />
        ) : (
          <RefreshingFrame refreshing={refreshing}>
            <section className="dash-outcomes" aria-labelledby="outcomes-heading" data-testid="dash-outcomes">
              <h2 id="outcomes-heading" className="dash-heading">
                Sync outcomes
              </h2>
              <p className="page-scope" data-testid="dash-scope">
                {scopeText(verdict.resolved.from, verdict.resolved.to)}
              </p>
              <KpiTrio view={summary} tin={tin} shortcutBranch={shortcut} period={period} onRetry={retrySummary} />
              <CoverageLine view={summary} />
              {shortcut !== null && (
                <p className="dash-shortcut" data-testid="dash-shortcut">
                  <Link to={branchPath(tin, shortcut)} period={period} testid="dash-shortcut-link">
                    {`Open branch ${shortcut}`}
                  </Link>
                </p>
              )}
              <DistributionBar view={summary} />
            </section>

            {branches.kind === 'loading' && <LoadingState label={`Loading branches for TPIN ${tin}…`} />}
            {branches.kind === 'error' && (
              <div data-testid="branches-error">
                <ErrorState message={branches.message} onRetry={retryBranches} />
              </div>
            )}
            {branches.kind === 'ready' && (
              <BranchList
                tin={tin}
                list={branches.list}
                total={total}
                more={branches.more}
                period={period}
                onLoadMore={loadMore}
              />
            )}
          </RefreshingFrame>
        )}
      </main>
    </div>
  )
}
